import { CreditCard, Shield, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PlanConfigDashboard from "../subscription/PlanConfigDashboard";
import ProfileContent from "./refactors/ProfileContent";

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = useMemo(
    () => [
      { id: "profile", label: "Profile", icon: User },
      // { id: "general", label: "General Settings", icon: Globe },
      // { id: "notifications", label: "Notifications", icon: Bell },
      { id: "security", label: "Security", icon: Shield },
      { id: "pricing", label: "Pricing", icon: CreditCard },
      // { id: "appearance", label: "Appearance", icon: Palette },
      // { id: "data", label: "Data Management", icon: Database },
    ],
    []
  );
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    tabs.some((tab) => tab.id === tabFromUrl) ? tabFromUrl! : "profile"
  );

  const handleTabChange = (id: string) => {
    setActiveTab(id);
    setSearchParams(id === "profile" ? {} : { tab: id }, { replace: true });
  };

  useEffect(() => {
    if (tabFromUrl && tabs.some((tab) => tab.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, tabs]);

  const renderContent = () => {
    switch (activeTab) {
      case "profile":
        return <ProfileContent />;
      case "general":
        return <GeneralContent />;
      case "notifications":
        return <NotificationsContent />;
      case "security":
        return <SecurityContent />;
      case "pricing":
        return <PricingContent />;
      case "appearance":
        return <AppearanceContent />;
      case "data":
        return <DataContent />;
      default:
        return <ProfileContent />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
      <div className="w-full mx-auto space-y-7">
        <div className="">
          <h1 className="text-[20px] font-bold text-gray-900 mb-2">Settings</h1>

          <div className="flex overflow-x-auto bg-white rounded-lg shadow-sm p-2 sticky">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`w-full min-w-max flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${
                    activeTab === tab.id
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1">
          <div className="">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
};

const GeneralContent = () => (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">General Settings</h2>

    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Company Name
        </label>
        <input
          type="text"
          defaultValue="Acme Corporation"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Time Zone
        </label>
        <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option>UTC-8 (Pacific Time)</option>
          <option>UTC-5 (Eastern Time)</option>
          <option>UTC+0 (GMT)</option>
          <option>UTC+1 (Central European Time)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Language
        </label>
        <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option>English (US)</option>
          <option>English (UK)</option>
          <option>Spanish</option>
          <option>French</option>
          <option>German</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date Format
        </label>
        <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option>MM/DD/YYYY</option>
          <option>DD/MM/YYYY</option>
          <option>YYYY-MM-DD</option>
        </select>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Regional Settings
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option>USD - US Dollar</option>
              <option>EUR - Euro</option>
              <option>GBP - British Pound</option>
              <option>JPY - Japanese Yen</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          Save Changes
        </button>
      </div>
    </div>
  </div>
);

const NotificationsContent = () => (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">
      Notification Settings
    </h2>

    <div className="space-y-6">
      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Email Notifications
        </h3>
        <div className="space-y-4">
          {[
            {
              label: "New user registrations",
              desc: "Get notified when a new user signs up",
            },
            {
              label: "System updates",
              desc: "Important updates about the platform",
            },
            {
              label: "Weekly reports",
              desc: "Receive weekly analytics and performance reports",
            },
            {
              label: "Security alerts",
              desc: "Critical security notifications",
            },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{item.label}</p>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked={idx < 2}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Push Notifications
        </h3>
        <div className="space-y-4">
          {[
            {
              label: "Desktop notifications",
              desc: "Show desktop notifications for important events",
            },
            {
              label: "Mobile push",
              desc: "Receive push notifications on your mobile device",
            },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{item.label}</p>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          Save Changes
        </button>
      </div>
    </div>
  </div>
);

const SecurityContent = () => (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">Security Settings</h2>

    <div className="space-y-6">
      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Password</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <input
              type="password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Update Password
          </button>
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Two-Factor Authentication
        </h3>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-medium text-gray-900">Enable 2FA</p>
            <p className="text-sm text-gray-500">
              Add an extra layer of security to your account
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" defaultChecked className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            Two-factor authentication is currently enabled for your account.
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Active Sessions
        </h3>
        <div className="space-y-3">
          {[
            {
              device: "Chrome on Windows",
              location: "San Francisco, CA",
              time: "Current session",
            },
            {
              device: "Safari on iPhone",
              location: "San Francisco, CA",
              time: "2 hours ago",
            },
            {
              device: "Firefox on Mac",
              location: "New York, NY",
              time: "1 day ago",
            },
          ].map((session, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900">{session.device}</p>
                <p className="text-sm text-gray-500">
                  {session.location} • {session.time}
                </p>
              </div>
              {idx !== 0 && (
                <button className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const PricingContent = () => (
  <div>
    <PlanConfigDashboard />
  </div>
);

const AppearanceContent = () => (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">
      Appearance Settings
    </h2>

    <div className="space-y-6">
      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Theme</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { name: "Light", selected: true },
            { name: "Dark", selected: false },
            { name: "Auto", selected: false },
          ].map((theme) => (
            <button
              key={theme.name}
              className={`p-4 border-2 rounded-lg transition-all ${
                theme.selected
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div
                className={`h-24 rounded mb-3 ${
                  theme.name === "Light"
                    ? "bg-white border-2 border-gray-200"
                    : theme.name === "Dark"
                    ? "bg-gray-900"
                    : "bg-gradient-to-br from-white to-gray-900"
                }`}
              ></div>
              <p className="font-medium text-gray-900">{theme.name}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Color Scheme
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {["blue", "purple", "green", "red", "orange", "pink"].map((color) => (
            <button
              key={color}
              className={`h-12 rounded-lg bg-${color}-600 hover:scale-110 transition-transform ${
                color === "blue" ? "ring-2 ring-offset-2 ring-blue-600" : ""
              }`}
            />
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Display Options
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Compact mode</p>
              <p className="text-sm text-gray-500">
                Reduce spacing throughout the interface
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Show animations</p>
              <p className="text-sm text-gray-500">
                Enable smooth transitions and effects
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" defaultChecked className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          Save Changes
        </button>
      </div>
    </div>
  </div>
);

const DataContent = () => (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">Data Management</h2>

    <div className="space-y-6">
      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Export Data
        </h3>
        <p className="text-gray-600 mb-4">
          Download a copy of your data in various formats
        </p>
        <div className="flex gap-3">
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            Export as CSV
          </button>
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            Export as JSON
          </button>
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            Export as PDF
          </button>
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Storage Usage
        </h3>
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Storage used</span>
            <span className="font-medium text-gray-900">45.2 GB of 100 GB</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full"
              style={{ width: "45%" }}
            ></div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Documents</p>
            <p className="text-2xl font-bold text-gray-900">12.4 GB</p>
          </div>
          <div className="border rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Media</p>
            <p className="text-2xl font-bold text-gray-900">28.6 GB</p>
          </div>
          <div className="border rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Other</p>
            <p className="text-2xl font-bold text-gray-900">4.2 GB</p>
          </div>
        </div>
      </div>

      <div className="border-b pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Data Retention
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activity Logs Retention
            </label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option>30 days</option>
              <option>90 days</option>
              <option>180 days</option>
              <option>1 year</option>
              <option>Forever</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Backup Frequency
            </label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option>Daily</option>
              <option>Weekly</option>
              <option>Monthly</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Danger Zone
        </h3>
        <div className="border-2 border-red-200 rounded-lg p-6 bg-red-50">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-red-900 mb-2">
              Clear All Data
            </h4>
            <p className="text-sm text-red-700 mb-4">
              Permanently delete all data associated with your account. This
              action cannot be undone.
            </p>
            <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              Clear All Data
            </button>
          </div>
          <div className="border-t border-red-200 pt-4">
            <h4 className="text-lg font-semibold text-red-900 mb-2">
              Delete Account
            </h4>
            <p className="text-sm text-red-700 mb-4">
              Once you delete your account, there is no going back. Please be
              certain.
            </p>
            <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default Settings;
